class SafeLoadScripts extends HTMLElement {
  constructor() {
    super();
    this._observer = null;
    this._idleHandle = null;
  }

  disconnectedCallback() {
    if (this._observer) {
      this._observer.disconnect();
      this._observer = null;
    }

    if (this._idleHandle) {
      const { type, id } = this._idleHandle;

      if (type === 'idle' && 'cancelIdleCallback' in window) {
        window.cancelIdleCallback(id);
      } else {
        clearTimeout(id);
      }

      this._idleHandle = null;
    }
  }

  connectedCallback() {
    const scripts = this.querySelectorAll('script');
    const loadMode = (this.dataset.load || 'immediate').toLowerCase();
    const rootMargin = this.dataset.rootMargin || '800px 0px';

    const loadScripts = () => {
      scripts.forEach((script) => {
        const key = script.dataset.flowLoadKey;
        const shouldDeduplicate = Boolean(key && window?.wetheme?.webcomponentRegistry?.checkScriptLoaded);

        const scriptLoaded = shouldDeduplicate
          ? window.wetheme.webcomponentRegistry.checkScriptLoaded(key)
          : false;

        if (scriptLoaded) return;

        if (shouldDeduplicate && window?.wetheme?.webcomponentRegistry?.register) {
          window.wetheme.webcomponentRegistry.register({ key });
        }

        const resolvedSrc = script.dataset.src || script.getAttribute('src');
        if (!resolvedSrc) return;

        const postLoadedScript = document.createElement('script');
        // Copy attributes across (except src/data-src which we handle explicitly)
        Array.from(script.attributes).forEach((attr) => {
          const name = attr.name.toLowerCase();
          if (name === 'src' || name === 'data-src') return;
          postLoadedScript.setAttribute(attr.name, attr.value);
        });

        postLoadedScript.src = resolvedSrc;

        this.parentElement.appendChild(postLoadedScript);
      });
    };

    if (loadMode === 'visible') {
      const target =
        this.closest('[data-wetheme-section-id]') ||
        this.closest('.shopify-section') ||
        this.parentElement;

      if (target && 'IntersectionObserver' in window) {
        this._observer = new IntersectionObserver(
          (entries) => {
            if (!entries.some((entry) => entry.isIntersecting)) return;
            this._observer?.disconnect();
            this._observer = null;
            loadScripts();
          },
          { rootMargin, threshold: 0 }
        );

        this._observer.observe(target);
        return;
      }

      loadScripts();
      return;
    }

    if (loadMode === 'idle') {
      const start = () => {
        this._idleHandle = null;
        if (!this.isConnected) return;
        loadScripts();
      };

      if ('requestIdleCallback' in window) {
        this._idleHandle = { type: 'idle', id: window.requestIdleCallback(start, { timeout: 2000 }) };
        return;
      }

      this._idleHandle = { type: 'timeout', id: window.setTimeout(start, 250) };
      return;
    }

    loadScripts();
  }
}

customElements.define('safe-load-scripts', SafeLoadScripts);
